import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

interface MetricCard {
  label: string;
  value: string | number;
  sublabel?: string;
  icon?: LucideIcon;
  iconColor?: string;
  cardBg?: string;
}

interface HeroSectionProps {
  title: string;
  description: string;
  metrics?: MetricCard[];
  actionButton?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  variant?: 'level1' | 'level2' | 'level3' | 'level4';
}

export function HeroSection({ 
  title, 
  description, 
  metrics, 
  actionButton,
  variant = 'level4'
}: HeroSectionProps) {
  // Level 1: Darkest - Main menu sections (#102a43 to #1e3a52)
  // Level 2: Dark - List pages (#1e3a52 to #2d4a5e)
  // Level 3: Medium - Individual workspaces (#486581 to #829ab1)
  // Level 4: Light - Tabs within workspaces (#f0f4f8 to #e8eef4)
  
  const isLight = variant === 'level4';
  const isLevel3 = variant === 'level3';
  
  const getBackgroundColor = () => {
    switch (variant) {
      case 'level1': return 'bg-gradient-to-br from-[#2A3B4C] to-[#3E5365]'; // Midnight to Ocean
      case 'level2': return 'bg-gradient-to-br from-[#3E5365] to-[#556B7C]'; // Ocean to Dusk
      case 'level3': return 'bg-gradient-to-br from-[#6F8394] to-[#8B9BAB]'; // Steel to Slate
      case 'level4': return 'bg-gradient-to-br from-[#E8E9E9] to-[#D5D9DD]'; // Ivory to Pearl
      default: return 'bg-gradient-to-br from-[#E8E9E9] to-[#D5D9DD]';
    }
  };
  
  return (
    <div className={`rounded-2xl mb-6 ${isLight ? 'p-8' : isLevel3 ? 'p-5' : 'p-6'} ${getBackgroundColor()}`}>
      {/* Header */}
      <div className={`flex items-start justify-between ${isLight ? 'mb-8' : isLevel3 ? 'mb-4' : 'mb-6'}`}>
        <div>
          <h1 className={`mb-2 ${isLight ? 'text-[34px] text-[#2A3B4C]' : isLevel3 ? 'text-[26px] text-white' : 'text-[31px] text-white'}`}>
            {title}
          </h1>
          <p className={isLight ? 'text-[18px] text-[#556B7C]' : isLevel3 ? 'text-[14px] text-[#E8E9E9]' : 'text-[15px] text-[#E8E9E9]'}>
            {description}
          </p>
        </div>
        {actionButton && (
          <button
            onClick={actionButton.onClick}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
              isLight
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                : 'border-2 border-white/30 text-white hover:bg-white/10'
            }`}
          >
            {actionButton.icon}
            <span>{actionButton.label}</span>
          </button>
        )}
      </div>

      {/* Metrics Grid */}
      {metrics && metrics.length > 0 && (
        <div className={`grid ${metrics.length === 4 ? 'grid-cols-4' : metrics.length === 3 ? 'grid-cols-3' : metrics.length === 5 ? 'grid-cols-5' : metrics.length === 6 ? 'grid-cols-6' : 'grid-cols-2'} ${isLevel3 ? 'gap-3' : 'gap-6'}`}>
          {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className={`rounded-xl transition-all ${isLight ? 'p-6' : isLevel3 ? 'p-3' : 'p-5'} ${
                isLight
                  ? `${metric.cardBg || 'bg-white'} shadow-md hover:shadow-lg border border-gray-100`
                  : 'bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15'
              }`}
            >
              {Icon && isLight && (
                <div className="flex items-start justify-between mb-4">
                  <div className="text-[14px] text-[#6F8394]">
                    {metric.label}
                  </div>
                  <div className={`p-2 rounded-lg ${metric.iconColor || 'bg-blue-50'}`}>
                    <Icon className={`w-5 h-5 ${metric.iconColor === 'bg-blue-50' ? 'text-blue-600' : metric.iconColor === 'bg-purple-50' ? 'text-purple-600' : metric.iconColor === 'bg-amber-50' ? 'text-amber-600' : metric.iconColor === 'bg-green-50' ? 'text-green-600' : metric.iconColor === 'bg-red-50' ? 'text-red-600' : 'text-blue-600'}`} />
                  </div>
                </div>
              )}
              {!isLight && (
                <div className={`${isLevel3 ? 'text-[12px]' : 'text-[14px]'} text-[#E8E9E9] ${isLevel3 ? 'mb-2' : 'mb-3'}`}>
                  {metric.label}
                </div>
              )}
              <div className={`leading-none ${isLight ? 'text-[30px] text-[#2A3B4C] mb-2' : isLevel3 ? 'text-[20px] text-white mb-0' : 'text-[24px] text-white mb-0'}`}>
                {metric.value}
              </div>
              {metric.sublabel && isLight && (
                <div className="text-[#6F8394] text-[13px]">{metric.sublabel}</div>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
}

